import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  RefreshCw, Search, Trash2, Download, Eye, X, FileText, GraduationCap,
  CheckCircle2, Clock, XCircle, MapPin, Phone, Mail,
} from 'lucide-react';
import { api, API_BASE, getToken } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const StatusBadge = ({ value }) => {
  const map = {
    pending: { bg: 'bg-[#FEF4DB]', tx: 'text-[#8a5e00]', icon: Clock, label: 'Pending' },
    approved: { bg: 'bg-[#E1F3E4]', tx: 'text-[#0B5E2A]', icon: CheckCircle2, label: 'Approved' },
    rejected: { bg: 'bg-[#FDE2E2]', tx: 'text-[#9b1c1c]', icon: XCircle, label: 'Rejected' },
  };
  const s = map[value] || map.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.tx}`}>
      <Icon className="h-3.5 w-3.5" /> {s.label}
    </span>
  );
};

const DetailDrawer = ({ app, onClose, onStatus, onDownloadPdf }) => {
  if (!app) return null;
  const fullName = `${app.title || ''} ${app.first_name || ''} ${app.last_name || ''}`.replace(/\s+/g, ' ').trim();
  const lengthParts = [
    app.program_length_hours && `${app.program_length_hours} hr`,
    app.program_length_weeks && `${app.program_length_weeks} wk`,
    app.program_length_months && `${app.program_length_months} mo`,
    app.program_length_years && `${app.program_length_years} yr`,
  ].filter(Boolean).join(' · ') || '—';

  const Row = ({ label, value }) => (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-[#0B5E2A]/5 last:border-0">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6b7e6d] col-span-1">{label}</div>
      <div className="text-sm text-[#1b2a1f] col-span-2 break-words">{value || '—'}</div>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <aside className="fixed right-0 top-0 bottom-0 z-50 w-full sm:max-w-2xl bg-white shadow-2xl overflow-y-auto" data-testid="domestic-app-drawer">
        <div className="border-b border-[#0B5E2A]/10 px-6 py-4 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-[#6b7e6d] font-semibold">Domestic Application</div>
            <h3 className="font-display text-xl text-[#0B5E2A]">{fullName || 'Applicant'}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-[#f0f6ee]" data-testid="drawer-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge value={app.status} />
            <span className="text-xs text-[#6b7e6d]">Submitted {new Date(app.created_at).toLocaleString()}</span>
          </div>

          <section>
            <h4 className="font-display text-sm uppercase tracking-[0.18em] text-[#0B5E2A] mb-2">Contact</h4>
            <Row label="Email" value={<span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {app.email}</span>} />
            <Row label="Phone" value={<span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {app.phone}</span>} />
            {app.alt_phone && <Row label="Alt Phone" value={app.alt_phone} />}
            <Row label="Date of Birth" value={app.dob} />
            <Row label="Gender" value={app.gender} />
            <Row label="Status in Country" value={app.status_in_country} />
            <Row label="Address" value={<span className="inline-flex items-start gap-1.5"><MapPin className="h-3.5 w-3.5 mt-0.5" /> {`${app.street || ''}, ${app.city || ''}, ${app.province || ''}, ${app.country || ''} ${app.postal || ''}`.replace(/^[ ,]+|[ ,]+$/g, '')}</span>} />
          </section>

          <section>
            <h4 className="font-display text-sm uppercase tracking-[0.18em] text-[#0B5E2A] mb-2">Program</h4>
            <Row label="Program Title" value={app.program_title} />
            <Row label="Campus" value={app.campus} />
            <Row label="Length" value={lengthParts} />
            <Row label="Start Date" value={`${app.start_month || ''} ${app.start_year || ''}`.trim() || '—'} />
          </section>

          <section>
            <h4 className="font-display text-sm uppercase tracking-[0.18em] text-[#0B5E2A] mb-2">Referral</h4>
            <Row label="Counsellor" value={app.counsellor} />
            <Row label="How did you hear about us?" value={app.heard_about} />
            <Row label="Agency" value={app.agency} />
          </section>

          <section>
            <h4 className="font-display text-sm uppercase tracking-[0.18em] text-[#0B5E2A] mb-2">Acknowledgements</h4>
            <Row label="Photo ID enclosed" value={app.ack_id ? 'Yes' : 'No'} />
            <Row label="Policies received" value={app.ack_policies ? 'Yes' : 'No'} />
            <Row label="Release authorized" value={app.ack_release ? 'Yes' : 'No'} />
            <Row label="Marketing opt-in" value={app.opt_marketing ? 'Yes' : 'No'} />
            <Row label="Signed by" value={`${app.applicant_name || ''} (${app.signed_date || '—'})`} />
          </section>

          <div className="flex flex-wrap gap-2 pt-2 sticky bottom-0 bg-white pb-2">
            {app.pdf?.storage_path && (
              <button onClick={() => onDownloadPdf(app.id)} data-testid="drawer-download-pdf"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#0B5E2A]/25 text-[#0B5E2A] font-semibold hover:bg-[#f0f6ee]">
                <Download className="h-4 w-4" /> Download PDF
              </button>
            )}
            <button onClick={() => onStatus(app.id, 'approved')} data-testid="drawer-approve"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#E1F3E4] text-[#0B5E2A] font-semibold hover:bg-[#c9e7ce]">
              <CheckCircle2 className="h-4 w-4" /> Approve
            </button>
            <button onClick={() => onStatus(app.id, 'rejected')} data-testid="drawer-reject"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#FDE2E2] text-[#9b1c1c] font-semibold hover:bg-[#fbcfcf]">
              <XCircle className="h-4 w-4" /> Reject
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

const DomesticAppsTab = ({ filter = '' }) => {
  const { toast } = useToast();
  const [apps, setApps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/domestic-applications');
      setApps(data || []);
    } catch (e) {
      toast({ title: 'Failed to load', description: e?.response?.data?.detail || 'Try again' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/domestic-applications/${id}`, { status });
      setApps((xs) => xs.map((a) => (a.id === id ? { ...a, status } : a)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast({ title: `Marked ${status}` });
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' });
    }
  };

  const deleteApp = async (a) => {
    if (!window.confirm(`Move "${a.first_name} ${a.last_name}"'s application to trash?`)) return;
    try {
      await api.delete(`/admin/domestic-applications/${a.id}`);
      setApps((xs) => xs.filter((x) => x.id !== a.id));
      if (selected?.id === a.id) setSelected(null);
      toast({ title: 'Moved to trash' });
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' });
    }
  };

  const downloadPdf = async (id) => {
    try {
      const url = `${API_BASE}/admin/domestic-applications/${id}/pdf`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement('a');
      const blobUrl = URL.createObjectURL(blob);
      a.href = blobUrl;
      a.download = `HCC-Application-${id}.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast({ title: 'Download failed', description: e.message });
    }
  };

  const filtered = useMemo(() => {
    const q = (filter || '').toLowerCase().trim();
    if (!q) return apps;
    return apps.filter((a) => [
      a.first_name, a.last_name, a.email, a.phone, a.program_title, a.campus, a.status,
    ].some((v) => String(v || '').toLowerCase().includes(q)));
  }, [apps, filter]);

  return (
    <div data-testid="domestic-apps-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm text-[#6b7e6d]">
          <FileText className="h-4 w-4" /> {filtered.length} application{filtered.length === 1 ? '' : 's'}
        </div>
        <button onClick={load} disabled={loading} data-testid="domestic-apps-refresh"
          className="inline-flex items-center gap-1.5 text-sm text-[#0B5E2A] hover:bg-[#f0f6ee] px-3 py-1.5 rounded">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-[#0B5E2A]/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f0f6ee] text-[#0B5E2A]">
              <tr>
                <th className="text-left font-semibold px-4 py-3">Applicant</th>
                <th className="text-left font-semibold px-4 py-3">Contact</th>
                <th className="text-left font-semibold px-4 py-3">Program</th>
                <th className="text-left font-semibold px-4 py-3">Campus</th>
                <th className="text-left font-semibold px-4 py-3">Status</th>
                <th className="text-left font-semibold px-4 py-3">Submitted</th>
                <th className="text-right font-semibold px-4 py-3 pr-6">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-[#6b7e6d]">
                  <GraduationCap className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  No domestic applications yet.
                </td></tr>
              )}
              {filtered.map((a) => (
                <tr key={a.id}
                  className="border-t border-[#0B5E2A]/10 hover:bg-[#faf8ee] cursor-pointer"
                  onClick={() => setSelected(a)}
                  data-testid={`domestic-row-${a.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{`${a.first_name} ${a.last_name}`.trim()}</div>
                    <div className="text-xs text-[#6b7e6d]">DOB {a.dob || '—'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div>{a.email}</div>
                    <div className="text-xs text-[#6b7e6d]">{a.phone}</div>
                  </td>
                  <td className="px-4 py-3"><div className="line-clamp-2 max-w-[260px]">{a.program_title || '—'}</div></td>
                  <td className="px-4 py-3">{a.campus || '—'}</td>
                  <td className="px-4 py-3"><StatusBadge value={a.status} /></td>
                  <td className="px-4 py-3 text-xs text-[#6b7e6d]">{new Date(a.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right pr-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                      <button onClick={() => setSelected(a)} title="View" className="h-7 w-7 rounded text-[#0B5E2A] hover:bg-[#E1F3E4] flex items-center justify-center"><Eye className="h-3.5 w-3.5" /></button>
                      {a.pdf?.storage_path && (
                        <button onClick={() => downloadPdf(a.id)} title="Download PDF"
                          data-testid={`domestic-download-${a.id}`}
                          className="h-7 w-7 rounded text-[#0B5E2A] hover:bg-[#E1F3E4] flex items-center justify-center"><Download className="h-3.5 w-3.5" /></button>
                      )}
                      <button onClick={() => updateStatus(a.id, 'approved')} className="px-2 py-1 text-xs rounded bg-[#E1F3E4] text-[#0B5E2A] font-semibold hover:bg-[#c9e7ce]">Approve</button>
                      <button onClick={() => updateStatus(a.id, 'rejected')} className="px-2 py-1 text-xs rounded bg-[#FDE2E2] text-[#9b1c1c] font-semibold hover:bg-[#fbcfcf]">Reject</button>
                      <button onClick={() => deleteApp(a)} title="Move to Trash"
                        data-testid={`domestic-delete-${a.id}`}
                        className="h-7 w-7 rounded text-[#9b1c1c] hover:bg-[#FDE2E2] flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <DetailDrawer
          app={selected}
          onClose={() => setSelected(null)}
          onStatus={updateStatus}
          onDownloadPdf={downloadPdf}
        />
      )}
    </div>
  );
};

export default DomesticAppsTab;
